"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DocumentStatusModal = void 0;
const react_1 = __importDefault(require("react"));
const doshx_controls_web_1 = require("doshx_controls_web");
const react_fontawesome_1 = require("@fortawesome/react-fontawesome");
const pro_light_svg_icons_1 = require("@fortawesome/pro-light-svg-icons");
const react_redux_1 = require("react-redux");
const formatDate_1 = require("../../../../../helpers/formatDate");
const STATUS_FLOW = [
    { label: "Draft", value: "DRAFT", icon: pro_light_svg_icons_1.faFileAlt, color: "text-green" },
    { label: "Sent", value: "SENT", icon: pro_light_svg_icons_1.faPaperPlane, color: "text-primary" },
    { label: "Viewed", value: "VIEWED", icon: pro_light_svg_icons_1.faEye, color: "text-orange" },
    { label: "Signing", value: "SIGNING", icon: pro_light_svg_icons_1.faPenNib, color: "text-yellow" },
    { label: "Completed", value: "COMPLETED", icon: pro_light_svg_icons_1.faCheckCircle, color: "text-primary" },
];
const DocumentStatusModal = ({ document, onView, onDelete, onClose, canDelete }) => {
    // Get current logged-in user for fallback
    const { user: currentUser } = (0, react_redux_1.useSelector)((state) => state.userState);
    // Find current status index
    const currentStatusIndex = STATUS_FLOW.findIndex((s) => s.value === document.status);
    // Get owner name from document (multiple sources)
    const ownerName = (() => {
        // Try document.owner first (populated object from backend)
        if (document.owner && typeof document.owner === 'object') {
            const name = `${document.owner.name || ""} ${document.owner.surname || ""}`.trim();
            return name || document.owner.email || "Document Owner";
        }
        // Try document.user_id (populated object from signed/unsigned documents)
        if (document.user_id && typeof document.user_id === 'object') {
            const userId = document.user_id;
            const name = `${userId.name || ""} ${userId.surname || ""}`.trim();
            return name || userId.email || "Document Owner";
        }
        // Fallback to current user (for drafts where owner = current user)
        if (currentUser) {
            const name = `${currentUser.name || ""} ${currentUser.surname || ""}`.trim();
            return name || currentUser.email || "Document Owner";
        }
        // Final fallback
        return "Document Owner";
    })();
    // Format creation date
    const createdDate = document.createdAt ? (0, formatDate_1.formatDateTime24hr)(document.createdAt) : "N/A";
    // Get signers info
    const signers = Array.isArray(document.signers) ? document.signers : [];
    // Get mobile-friendly status flow (show 3 icons max)
    const getMobileStatusFlow = () => {
        if (currentStatusIndex === -1)
            return STATUS_FLOW.slice(0, 3);
        // If at start (index 0 or 1): show first 3
        if (currentStatusIndex <= 1) {
            return [
                STATUS_FLOW[0],
                STATUS_FLOW[1],
                STATUS_FLOW[2],
                { label: "More", value: "MORE", icon: pro_light_svg_icons_1.faEllipsis, color: "text-gray-400" }
            ];
        }
        // If at end (index 3 or 4): show last 3
        if (currentStatusIndex >= 3) {
            return [
                { label: "More", value: "MORE", icon: pro_light_svg_icons_1.faEllipsis, color: "text-gray-400" },
                STATUS_FLOW[2],
                STATUS_FLOW[3],
                STATUS_FLOW[4]
            ];
        }
        // If in middle (index 2): show current with prev/next
        return [
            { label: "More", value: "MORE", icon: pro_light_svg_icons_1.faEllipsis, color: "text-gray-400" },
            STATUS_FLOW[1],
            STATUS_FLOW[2],
            STATUS_FLOW[3],
            { label: "More", value: "MORE", icon: pro_light_svg_icons_1.faEllipsis, color: "text-gray-400" }
        ];
    };
    // Get button label based on document status
    const getViewButtonLabel = () => {
        if (document.status === "DRAFT")
            return "Edit";
        if (document.status === "SENT" || document.status === "VIEWED")
            return "View/Sign";
        if (document.status === "SIGNING")
            return "View";
        if (document.status === "COMPLETED")
            return "View";
        return "View";
    };
    return (react_1.default.createElement("div", { className: "flex flex-col gap-6 p-2" },
        react_1.default.createElement("div", { className: "flex flex-col gap-2" },
            react_1.default.createElement("div", { className: "text-sm text-gray-600" },
                react_1.default.createElement("div", null,
                    react_1.default.createElement("strong", null, "Created:"),
                    " ",
                    createdDate),
                react_1.default.createElement("div", null,
                    react_1.default.createElement("strong", null, "Owner:"),
                    " ",
                    ownerName))),
        react_1.default.createElement("div", { className: "hidden md:flex flex-col gap-3" },
            react_1.default.createElement(doshx_controls_web_1.LabelControl, { label: "Status Flow", bold: true, size: "medium" }),
            react_1.default.createElement("div", { className: "flex items-center justify-between px-4" }, STATUS_FLOW.map((status, index) => {
                const isPast = index < currentStatusIndex;
                const isCurrent = index === currentStatusIndex;
                const isFuture = index > currentStatusIndex;
                return (react_1.default.createElement(react_1.default.Fragment, { key: status.value },
                    react_1.default.createElement("div", { className: "flex flex-col items-center gap-2" },
                        react_1.default.createElement("div", { className: `flex items-center justify-center rounded-full border-2 ${isCurrent
                                ? `${status.color} border-current w-14 h-14`
                                : isPast
                                    ? "text-success border-success w-10 h-10"
                                    : "text-gray-300 border-gray-300 w-10 h-10"} transition-all` },
                            react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: isPast ? pro_light_svg_icons_1.faCheck : status.icon, className: `${isCurrent ? "text-2xl" : "text-xl"}` })),
                        react_1.default.createElement("div", { className: `text-xs ${isCurrent ? `font-bold ${status.color}` : isPast ? "text-success" : "text-gray-400"}` }, status.label)),
                    index < STATUS_FLOW.length - 1 && react_1.default.createElement("div", { className: `flex-1 h-0.5 mx-2 ${index < currentStatusIndex ? "bg-success" : "bg-gray-300"}` })));
            }))),
        react_1.default.createElement("div", { className: "flex md:hidden flex-col gap-3" },
            react_1.default.createElement(doshx_controls_web_1.LabelControl, { label: "Status Flow", bold: true, size: "medium" }),
            react_1.default.createElement("div", { className: "flex items-center justify-between px-2" }, getMobileStatusFlow().map((status, index, array) => {
                const fullStatusIndex = STATUS_FLOW.findIndex(s => s.value === status.value);
                const isPast = fullStatusIndex !== -1 && fullStatusIndex < currentStatusIndex;
                const isCurrent = fullStatusIndex === currentStatusIndex;
                const isEllipsis = status.value === "MORE";
                return (react_1.default.createElement(react_1.default.Fragment, { key: `${status.value}-${index}` },
                    react_1.default.createElement("div", { className: "flex flex-col items-center gap-1" },
                        react_1.default.createElement("div", { className: `flex items-center justify-center rounded-full ${isEllipsis
                                ? "w-8 h-8"
                                : isCurrent
                                    ? `${status.color} border-2 border-current w-12 h-12`
                                    : isPast
                                        ? "border-2 text-success border-success w-8 h-8"
                                        : "border-2 text-gray-300 border-gray-300 w-8 h-8"} transition-all` },
                            react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: isPast && !isEllipsis ? pro_light_svg_icons_1.faCheck : status.icon, className: `${isCurrent ? "text-xl" : "text-base"}` })),
                        !isEllipsis && (react_1.default.createElement("div", { className: `text-[10px] ${isCurrent ? `font-bold ${status.color}` : isPast ? "text-success" : "text-gray-400"}` }, status.label))),
                    index < array.length - 1 && react_1.default.createElement("div", { className: `flex-1 h-0.5 mx-1 ${isPast && !isEllipsis ? "bg-success" : "bg-gray-300"}` })));
            }))),
        signers.length > 0 && (react_1.default.createElement("div", { className: "flex flex-col gap-3 max-h-[300px] overflow-y-auto pr-1", style: { scrollbarWidth: 'thin', scrollbarColor: '#cbd5e0 #f7fafc' } },
            react_1.default.createElement(doshx_controls_web_1.LabelControl, { label: "Signers", bold: true, size: "medium" }),
            react_1.default.createElement("div", { className: "flex flex-col gap-2" }, signers.map((signer, index) => {
                const name = `${signer.first_name || ""} ${signer.last_name || ""}`.trim() || signer.email;
                const hasViewed = !!signer.viewedAt;
                const hasCompleted = !!signer.completedAt;
                return (react_1.default.createElement("div", { key: index, className: "flex flex-col md:flex-row md:items-start md:justify-between gap-2 p-3 border border-borders rounded-md bg-white" },
                    react_1.default.createElement("div", { className: "flex flex-col gap-1" },
                        react_1.default.createElement("div", { className: "font-medium text-sm" }, name),
                        react_1.default.createElement("div", { className: "text-xs text-gray-500" }, signer.email)),
                    react_1.default.createElement("div", { className: "flex flex-col gap-2" },
                        react_1.default.createElement("div", { className: "flex items-center gap-2" },
                            react_1.default.createElement("span", { className: "text-xs text-gray-600 min-w-[70px]" }, "Viewed:"),
                            hasViewed ? (react_1.default.createElement("div", { className: "flex items-center gap-1 text-success" },
                                react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: pro_light_svg_icons_1.faCheck, className: "text-xs" }),
                                react_1.default.createElement("span", { className: "text-xs" }, (0, formatDate_1.formatDateTime24hr)(signer.viewedAt)))) : (react_1.default.createElement("div", { className: "flex items-center gap-1 text-gray-400" },
                                react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: pro_light_svg_icons_1.faTimes, className: "text-xs" }),
                                react_1.default.createElement("span", { className: "text-xs" }, "Pending")))),
                        react_1.default.createElement("div", { className: "flex items-center gap-2" },
                            react_1.default.createElement("span", { className: "text-xs text-gray-600 min-w-[70px]" }, "Completed:"),
                            hasCompleted ? (react_1.default.createElement("div", { className: "flex items-center gap-1 text-success" },
                                react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: pro_light_svg_icons_1.faCheck, className: "text-xs" }),
                                react_1.default.createElement("span", { className: "text-xs" }, (0, formatDate_1.formatDateTime24hr)(signer.completedAt)))) : (react_1.default.createElement("div", { className: "flex items-center gap-1 text-gray-400" },
                                react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: pro_light_svg_icons_1.faTimes, className: "text-xs" }),
                                react_1.default.createElement("span", { className: "text-xs" }, "Pending")))))));
            })))),
        document.only_signer && signers.length === 0 && (react_1.default.createElement("div", { className: "text-center text-sm text-gray-500 p-4 bg-gray-50 rounded-md" }, "This document is for the owner to sign only")),
        react_1.default.createElement("div", { className: "flex justify-between md:justify-end gap-2 pt-4 border-t border-borders" },
            canDelete && onDelete && (react_1.default.createElement(doshx_controls_web_1.ButtonControl, { label: "Delete", size: "small", backgroundColor: "bg-white", textColor: "text-error", borderColor: "border-error", onClick: onDelete })),
            onView && (react_1.default.createElement(doshx_controls_web_1.ButtonControl, { label: getViewButtonLabel(), size: "small", backgroundColor: "bg-secondary", textColor: "text-white", onClick: onView })),
            onClose && (react_1.default.createElement(doshx_controls_web_1.ButtonControl, { label: "Close", size: "small", backgroundColor: "bg-white", textColor: "text-black", borderColor: "border-borders", onClick: onClose })))));
};
exports.DocumentStatusModal = DocumentStatusModal;
